import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const NEWSLETTER_URL = "https://readdy.ai/api/form/d7ihj4ehdlg6dog9deug";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation();
  const footerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = footerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.style.opacity = "1";
            el.style.transform = "translateY(0)";
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.05 }
    );
    el.style.opacity = "0";
    el.style.transform = "translateY(20px)";
    el.style.transition = "opacity 0.7s ease, transform 0.7s ease";
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams({ email });
      await fetch(NEWSLETTER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
      setEmail("");
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const quickLinks = [
    { labelKey: "nav.home", path: "/" },
    { labelKey: "nav.mission", path: "/mission" },
    { labelKey: "nav.projects", path: "/projects" },
    { labelKey: "nav.stories", path: "/stories" },
    { labelKey: "nav.volunteer", path: "/volunteer" },
    { labelKey: "nav.donate", path: "/donate" },
    { labelKey: "nav.contact", path: "/contact" },
  ];

  const getInvolvedLinks = [
    "Volunteer Opportunities",
    "Corporate Partnerships",
    "Fundraise for CAF",
    "Annual Reports",
    "Financial Transparency",
    "News & Updates",
  ];

  return (
    <footer className="bg-[#1B2838] text-white" ref={footerRef}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#4ECDC4] text-[#1B2838] font-bold text-sm">
                CAF
              </div>
              <div>
                <div className="font-bold text-sm leading-tight">Canada Afghanistan</div>
                <div className="text-[#4ECDC4] text-xs">Foundation</div>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">{t("footer.tagline")}</p>
            <p className="text-gray-500 text-xs">{t("footer.charity")}</p>
            <p className="text-gray-500 text-xs mt-1">{t("footer.est")}</p>
            <div className="flex items-center gap-3 mt-5">
              {["ri-facebook-fill", "ri-twitter-x-fill", "ri-instagram-line", "ri-linkedin-fill"].map(
                (icon) => (
                  <a
                    key={icon}
                    href="#"
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 text-gray-400 hover:bg-[#4ECDC4] hover:text-[#1B2838] transition-all cursor-pointer"
                  >
                    <i className={`${icon} text-sm`}></i>
                  </a>
                )
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-widest text-gray-300 mb-5">
              <a href="#quick-links">{t("footer.quickLinks")}</a>
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-400 text-sm hover:text-[#4ECDC4] transition-colors cursor-pointer"
                  >
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Get Involved */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-widest text-gray-300 mb-5">
              <a href="#get-involved">{t("footer.getInvolved")}</a>
            </h4>
            <ul className="space-y-3">
              {getInvolvedLinks.map((item) => (
                <li key={item}>
                  <a
                    href="#"
                    className="text-gray-400 text-sm hover:text-[#4ECDC4] transition-colors cursor-pointer"
                  >
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-widest text-gray-300 mb-5">
              <a href="#stay-connected">{t("footer.stayConnected")}</a>
            </h4>
            <p className="text-gray-400 text-sm mb-4 leading-relaxed">{t("footer.newsletter.desc")}</p>
            {submitted ? (
              <div className="bg-[#4ECDC4]/20 border border-[#4ECDC4]/30 rounded-lg px-4 py-3 text-[#4ECDC4] text-sm">
                <i className="ri-check-line mr-2"></i>
                {t("footer.newsletter.success")}
              </div>
            ) : (
              <form onSubmit={handleNewsletter} data-readdy-form className="flex flex-col gap-3">
                <div className="flex rounded-lg overflow-hidden border border-white/10">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("footer.newsletter.placeholder")}
                    required
                    className="flex-1 bg-white/10 text-white placeholder-gray-500 px-4 py-2.5 text-sm outline-none min-w-0"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-[#4ECDC4] text-[#1B2838] px-4 py-2.5 font-bold text-sm whitespace-nowrap cursor-pointer hover:bg-[#3dbdb5] transition-colors"
                  >
                    {submitting ? (
                      <i className="ri-loader-4-line animate-spin"></i>
                    ) : (
                      <i className="ri-arrow-right-line"></i>
                    )}
                  </button>
                </div>
                <p className="text-gray-500 text-xs">{t("footer.newsletter.privacy")}</p>
              </form>
            )}

            <div className="mt-6 pt-5 border-t border-white/10">
              <p className="text-gray-400 text-xs mb-2">{t("footer.support")}</p>
              <div className="flex flex-wrap gap-2">
                {["English", "Pashto", "Dari", "Français", "Deutsch"].map((lang) => (
                  <span
                    key={lang}
                    className="text-xs bg-white/10 text-gray-300 px-2 py-1 rounded-full"
                  >
                    {lang}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-gray-500 text-xs">
            &copy; {new Date().getFullYear()} {t("footer.copyright")}
          </p>
          <div className="flex items-center gap-4">
            {(["footer.privacy", "footer.terms", "footer.accessibility"] as const).map(
              (key, i) => (
                <span key={key} className="flex items-center gap-4">
                  <a href="#" className="text-gray-500 text-xs hover:text-gray-300 cursor-pointer">
                    {t(key)}
                  </a>
                  {i < 2 && <span className="text-gray-700">|</span>}
                </span>
              )
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
